import { cityContext } from '../../services/cityContext';
import { marked } from 'marked';


interface ProcessedMessage {
  textContent: string;
  jsonContent: string | null;
  weatherLocation?: string;
  itineraryPlan?: {
    destination: string;
    startDate: string;
    endDate: string;
    travelGroup: string;
    interests: string[];
    action?: string;
  } | null;
}


export function processStreamingMessage(content: string): ProcessedMessage {
  return {
    textContent: content,
    jsonContent: null,
    weatherLocation: undefined,
    itineraryPlan: null
  };
}

export function processStreamingMessage_UNUSED1(content: string): ProcessedMessage {
  try {
    // Check for weather first
    const weatherKeywords = /weather|temperature|forecast|climate/i;
    
    if (weatherKeywords.test(content)) {
      const checkWeatherMatch = content.match(/Let me check (?:the )?(?:weather|temperature|forecast|climate)(?: in| at| for)?\s+([^.!?\n]+)/i);
      const weatherMatch = content.match(/(?:weather|temperature|forecast|climate).*?(?:in|at|for)\s+([^.!?,\n]+)/i);
      
      let weatherLocation = (checkWeatherMatch || weatherMatch)?.[1]?.trim();
      
      if (weatherLocation) {
        const locationParts = weatherLocation.split(',');
        let cityPart = locationParts[0].trim();
        
        // Clean up the city name
        cityPart = cityPart
          .replace(/(?:restaurant|cafe|hotel|the|bar|grill|pub|bistro|lounge)\b/gi, '')
          .replace(/^[\s\W]+|[\s\W]+$/g, '')
          .trim();
        
        return {
          textContent: content,
          jsonContent: null,
          weatherLocation: cityPart
        };
      }
    }

    // Look for any JSON markers
    const jsonMarkers = [
      '{\\s*"locations":', 
      '{\\s*"name":', 
      '{\\s*"city":', 
      '{\\s*"country":', 
      '{\\s*"coordinates":', 
      '{\\s*"itineraryPlan":'
    ];
    
    const jsonRegex = new RegExp(jsonMarkers.join('|'), 'i');
    const jsonStart = content.match(jsonRegex);
    
    // If JSON marker found, immediately cut text there
    if (jsonStart && jsonStart.index !== undefined) {
      const splitIndex = jsonStart.index;
      const cleanText = content.substring(0, splitIndex).trim();
      const jsonPortion = content.substring(splitIndex);
      
      // Try to find complete JSON
      let extractedJson = null;
      let itineraryPlan = null;
      
      // Check for location standard format
      const locationJsonRegex = /{\s*"locations":\s*\[[\s\S]*?\]\s*}/;
      const locationMatch = jsonPortion.match(locationJsonRegex);
      
      if (locationMatch) {
        try {
          const parsedJson = JSON.parse(locationMatch[0]);
          const locations = parsedJson.locations || [];
          if (locations.length > 0 && locations[0].city) {
            cityContext.setCurrentCity(locations[0].city);
          }
          extractedJson = locationMatch[0];
        } catch (e) {}
      }
      
      // Check for single location format
      if (!extractedJson) {
        const singleLocationRegex = /{\s*"(?:name|city|country|coordinates|rating|reviews)"[\s\S]*?}/;
        const singleMatch = jsonPortion.match(singleLocationRegex);
        
        if (singleMatch) {
          try {
            const locationObj = JSON.parse(singleMatch[0]);
            
            if (locationObj.name && locationObj.coordinates) {
              extractedJson = JSON.stringify({ locations: [locationObj] });
              
              if (locationObj.city) {
                cityContext.setCurrentCity(locationObj.city);
              } else if (locationObj.name) {
                cityContext.setCurrentCity(locationObj.name);
              }
            }
          } catch (e) {}
        }
      }
      
      // Check for itinerary plan
      const itineraryRegex = /\{\s*"itineraryPlan"\s*:\s*\{[\s\S]*?\}\s*(?:,\s*"action"\s*:\s*"[^"]*")?\s*\}/;
      const itineraryMatch = jsonPortion.match(itineraryRegex);
      
      if (itineraryMatch) {
        try {
          const parsedData = JSON.parse(itineraryMatch[0]);
          itineraryPlan = parsedData.itineraryPlan;
          
          if (itineraryPlan && itineraryPlan.destination) {
            cityContext.setCurrentCity(itineraryPlan.destination);
          }
        } catch (e) {}
      }
      
      // Format text content
      const formattedText = formatTextContent(cleanText);
      
      return {
        textContent: cleanText,//formattedText,
        jsonContent: extractedJson,
        itineraryPlan
      };
    }
    
    // No JSON detected, just format the text
    return {
      textContent: content,//formatTextContent(content),
      jsonContent: null
    };
  } catch (error) {
    // Safe fallback - remove any JSON-like content
    return { 
      textContent: content.replace(/{\s*".*$/g, '').trim(), 
      jsonContent: null 
    };
  }
}

function formatTextContent_UNUSED(text: string): string {
  // Remove URLs
  let formattedText = text.replace(/https?:\/\/[^\s\)]+/g, '');
  
  // Format lists properly for markdown
  formattedText = formattedText
    // Format numbered lists
    .replace(/(?:^|\n)(\s*\d+\.\s+)/g, '\n\n$1')
    // Format bullet points
    .replace(/(?:^|\n)(\s*[-•]\s+)/g, '\n\n$1')
    // Ensure proper spacing
    .replace(/(\d+\.|\-|\•)\s+([^\n]+)(?!\n)/g, '$1 $2\n')
    // Clean up whitespace
    .trim();
  
  return formattedText;
}

function formatTextContent(text: string): string {
  // Remove URLs
  let formattedText = text.replace(/https?:\/\/[^\s\)]+/g, '');
  
  // Format lists properly for markdown
  formattedText = formattedText
    // Ensure numbered lists have proper formatting
    .replace(/^(\d+)\.\s*/gm, '$1. ')
    .replace(/\n(\d+)\.\s*/g, '\n\n$1. ')
    // Format bullet points (handle various bullet styles)
    .replace(/^[•·▪▫◦‣⁃]\s*/gm, '- ')
    .replace(/\n[•·▪▫◦‣⁃]\s*/g, '\n\n- ')
    // Convert asterisk bullets to dashes
    .replace(/^\*\s+/gm, '- ')
    .replace(/\n\*\s+/g, '\n\n- ')
    // Ensure proper spacing after list markers
    .replace(/^(\d+\.|[-*])\s*(\S)/gm, '$1 $2')
    // Remove excessive newlines
    .replace(/\n{3,}/g, '\n\n')
    // Clean up whitespace
    .trim();
  
  return formattedText;
}